import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import { db } from "./config";
import {
  getUserPortfolios,
  getPortfolioInvestments,
  getInvestmentTransactions,
  getPortfolioTransactions,
  updateInvestment,
  getPortfolioCashAvailable,
} from "./firestoreService";

// ===== ENHANCED PORTFOLIO OPERATIONS =====

// Create a new portfolio with enhanced structure
export const createPortfolioEnhanced = async (userId, portfolioData) => {
  try {
    const docRef = await addDoc(collection(db, "portfolios"), {
      userId,
      name: portfolioData.name || "My Portfolio",
      description: portfolioData.description || "",
      currency: portfolioData.currency || "USD",
      isActive: true,
      // Calculated fields (updated on transactions)
      totalValue: 0,
      totalInvested: 0,
      totalReturn: 0,
      returnPercentage: 0,
      cashAvailable: portfolioData.cashAvailable || 0,
      // Performance tracking
      performanceHistory: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log("Enhanced portfolio created with ID:", docRef.id);
    return { success: true, portfolioId: docRef.id };
  } catch (error) {
    console.error("Error creating enhanced portfolio:", error);
    return { success: false, error: error.message };
  }
};

// Get portfolio with calculated stats
export const getPortfolioWithStats = async (portfolioId) => {
  try {
    const portfolioDoc = await getDoc(doc(db, "portfolios", portfolioId));
    if (!portfolioDoc.exists()) {
      return { success: false, error: "Portfolio not found" };
    }

    const portfolio = { id: portfolioDoc.id, ...portfolioDoc.data() };

    // Get all investments for this portfolio
    const investmentsResult = await getPortfolioInvestments(portfolioId);
    if (investmentsResult.success) {
      portfolio.investments = investmentsResult.data;

      // Calculate totals from investments
      const totals = portfolio.investments.reduce(
        (acc, inv) => {
          acc.totalValue += inv.currentValue || 0;
          acc.totalInvested += inv.totalCost || 0;
          acc.totalReturn += inv.totalReturn || 0;
          return acc;
        },
        { totalValue: 0, totalInvested: 0, totalReturn: 0 }
      );

      // Add cash to total value
      totals.totalValue += portfolio.cashAvailable || 0;

      portfolio.calculatedStats = totals;
    }

    return { success: true, data: portfolio };
  } catch (error) {
    console.error("Error getting portfolio with stats:", error);
    return { success: false, error: error.message };
  }
};

// ===== ENHANCED INVESTMENT OPERATIONS =====

// Add investment with calculated fields
export const addInvestmentEnhanced = async (portfolioId, investmentData) => {
  try {
    const docRef = await addDoc(collection(db, "investments"), {
      portfolioId,
      symbol: investmentData.symbol,
      name: investmentData.name,
      type: investmentData.type || "stock",
      currency: investmentData.currency || "USD",
      // Calculated fields (updated from transactions)
      totalShares: 0,
      averageCost: 0,
      totalCost: 0,
      currentPrice: investmentData.currentPrice || 0,
      currentValue: 0,
      totalReturn: 0,
      returnPercentage: 0,
      // Performance tracking
      performanceHistory: [],
      notes: investmentData.notes || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log("Enhanced investment added with ID:", docRef.id);
    return { success: true, investmentId: docRef.id };
  } catch (error) {
    console.error("Error adding enhanced investment:", error);
    return { success: false, error: error.message };
  }
};

// Get investment with full details and history
export const getInvestmentDetails = async (investmentId) => {
  try {
    const investmentDoc = await getDoc(doc(db, "investments", investmentId));
    if (!investmentDoc.exists()) {
      return { success: false, error: "Investment not found" };
    }

    const investment = { id: investmentDoc.id, ...investmentDoc.data() };

    // Get all transactions for this investment
    const transactionsResult = await getInvestmentTransactions(
      investment.portfolioId,
      investmentId
    );

    if (transactionsResult.success) {
      investment.transactions = transactionsResult.data;

      // Calculate current position from transactions
      const position = calculatePositionFromTransactions(
        transactionsResult.data
      );
      investment.calculatedPosition = position;
    }

    return { success: true, data: investment };
  } catch (error) {
    console.error("Error getting investment details:", error);
    return { success: false, error: error.message };
  }
};

// ===== ENHANCED TRANSACTION OPERATIONS =====

// Fallback function that doesn't use transactions (for concurrency issues)
export const addTransactionSimple = async (portfolioId, transactionData) => {
  try {
    // Handle date conversion
    let transactionDate;
    if (transactionData.date) {
      try {
        if (typeof transactionData.date === "string") {
          const parsedDate = new Date(transactionData.date);
          if (!isNaN(parsedDate.getTime())) {
            transactionDate = parsedDate;
          } else {
            transactionDate = serverTimestamp();
          }
        } else if (transactionData.date instanceof Date) {
          if (!isNaN(transactionData.date.getTime())) {
            transactionDate = transactionData.date;
          } else {
            transactionDate = serverTimestamp();
          }
        } else {
          transactionDate = serverTimestamp();
        }
      } catch (error) {
        console.warn("Invalid date provided, using serverTimestamp:", error);
        transactionDate = serverTimestamp();
      }
    } else {
      transactionDate = serverTimestamp();
    }

    // 1. Add the transaction
    const txnRef = doc(collection(db, "transactions"));
    const transactionDoc = {
      portfolioId,
      investmentId: transactionData.investmentId,
      symbol: transactionData.symbol,
      type: transactionData.type,
      shares: transactionData.shares || 0,
      price: transactionData.price || 0,
      totalAmount: transactionData.totalAmount || 0,
      fees: transactionData.fees || 0,
      currency: transactionData.currency || "USD",
      date: transactionDate,
      notes: transactionData.notes || "",
      source: transactionData.source || "manual",
      tags: transactionData.tags || [],
      createdAt: serverTimestamp(),
    };

    // Only add optional fields if they have valid values
    if (
      transactionData.dividendPerShare !== undefined &&
      transactionData.dividendPerShare !== null
    ) {
      transactionDoc.dividendPerShare = transactionData.dividendPerShare;
    }
    if (
      transactionData.splitRatio !== undefined &&
      transactionData.splitRatio !== null
    ) {
      transactionDoc.splitRatio = transactionData.splitRatio;
    }

    await setDoc(txnRef, transactionDoc);

    // 2. Update investment position (if investmentId provided)
    if (transactionData.investmentId) {
      const investmentRef = doc(
        db,
        "investments",
        transactionData.investmentId
      );
      const investmentSnap = await getDoc(investmentRef);

      if (investmentSnap.exists()) {
        const investment = investmentSnap.data();
        const updatedPosition = calculateUpdatedPosition(
          investment,
          transactionDoc
        );

        await updateDoc(investmentRef, {
          ...updatedPosition,
          updatedAt: serverTimestamp(),
        });
      }
    }

    // 3. Update portfolio totals
    const portfolioRef = doc(db, "portfolios", portfolioId);
    const portfolioSnap = await getDoc(portfolioRef);

    if (portfolioSnap.exists()) {
      const portfolio = portfolioSnap.data();
      const updatedPortfolio = calculateUpdatedPortfolio(
        portfolio,
        transactionDoc
      );

      await updateDoc(portfolioRef, {
        ...updatedPortfolio,
        updatedAt: serverTimestamp(),
      });
    }

    return { success: true, transactionId: txnRef.id };
  } catch (error) {
    console.error("Error adding simple transaction:", error);
    return { success: false, error: error.message };
  }
};

// Fast transaction function - uses simple method directly for better performance
export const addTransactionFast = async (portfolioId, transactionData) => {
  try {
    // Use simple method directly for faster performance
    return await addTransactionSimple(portfolioId, transactionData);
  } catch (error) {
    console.error("Error adding fast transaction:", error);
    return { success: false, error: error.message };
  }
};

// Add transaction and update investment/portfolio automatically
export const addTransactionEnhanced = async (portfolioId, transactionData) => {
  const maxRetries = 2; // Reduced from 3 to 2 for faster fallback
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      const result = await runTransaction(db, async (transaction) => {
        const batch = writeBatch(db);

        // Handle date conversion with better validation
        let transactionDate;
        if (transactionData.date) {
          try {
            // If it's an ISO string, convert to Date object
            if (typeof transactionData.date === "string") {
              const parsedDate = new Date(transactionData.date);
              // Check if the date is valid
              if (!isNaN(parsedDate.getTime())) {
                transactionDate = parsedDate;
              } else {
                transactionDate = serverTimestamp();
              }
            } else if (transactionData.date instanceof Date) {
              // Check if the Date object is valid
              if (!isNaN(transactionData.date.getTime())) {
                transactionDate = transactionData.date;
              } else {
                transactionDate = serverTimestamp();
              }
            } else {
              transactionDate = serverTimestamp();
            }
          } catch (error) {
            console.warn(
              "Invalid date provided, using serverTimestamp:",
              error
            );
            transactionDate = serverTimestamp();
          }
        } else {
          transactionDate = serverTimestamp();
        }

        // 1. Add the transaction - filter out undefined values
        const txnRef = doc(collection(db, "transactions"));
        const transactionDoc = {
          portfolioId,
          investmentId: transactionData.investmentId,
          symbol: transactionData.symbol,
          type: transactionData.type, // buy, sell, dividend, split
          shares: transactionData.shares || 0,
          price: transactionData.price || 0,
          totalAmount: transactionData.totalAmount || 0,
          fees: transactionData.fees || 0,
          currency: transactionData.currency || "USD",
          date: transactionDate,
          notes: transactionData.notes || "",
          // Metadata
          source: transactionData.source || "manual",
          tags: transactionData.tags || [],
          createdAt: serverTimestamp(),
        };

        // Only add optional fields if they have valid values
        if (
          transactionData.dividendPerShare !== undefined &&
          transactionData.dividendPerShare !== null
        ) {
          transactionDoc.dividendPerShare = transactionData.dividendPerShare;
        }
        if (
          transactionData.splitRatio !== undefined &&
          transactionData.splitRatio !== null
        ) {
          transactionDoc.splitRatio = transactionData.splitRatio;
        }

        batch.set(txnRef, transactionDoc);

        // 2. Update investment position (only if investmentId is provided)
        if (transactionData.investmentId) {
          const investmentRef = doc(
            db,
            "investments",
            transactionData.investmentId
          );
          const investmentSnap = await transaction.get(investmentRef);

          if (investmentSnap.exists()) {
            const investment = investmentSnap.data();
            const updatedPosition = calculateUpdatedPosition(
              investment,
              transactionDoc
            );

            batch.update(investmentRef, {
              ...updatedPosition,
              updatedAt: serverTimestamp(),
            });
          }
        }

        // 3. Update portfolio totals
        const portfolioRef = doc(db, "portfolios", portfolioId);
        const portfolioSnap = await transaction.get(portfolioRef);

        if (portfolioSnap.exists()) {
          const portfolio = portfolioSnap.data();
          const updatedPortfolio = calculateUpdatedPortfolio(
            portfolio,
            transactionDoc
          );

          batch.update(portfolioRef, {
            ...updatedPortfolio,
            updatedAt: serverTimestamp(),
          });
        }

        await batch.commit();
        return txnRef.id;
      });

      return { success: true, transactionId: result };
    } catch (error) {
      attempt++;
      console.warn(`Transaction attempt ${attempt} failed:`, error.message);

      // If it's a version conflict and we haven't exceeded retries, try again
      if (error.code === "failed-precondition" && attempt < maxRetries) {
        // Reduced wait time for faster fallback
        await new Promise((resolve) => setTimeout(resolve, 50 * attempt));
        continue;
      }

      // If we've exhausted retries, fall back to simple method immediately
      if (attempt >= maxRetries) {
        console.warn(
          "Transaction failed after retries, falling back to simple method"
        );
        return await addTransactionSimple(portfolioId, transactionData);
      }

      // If it's a different error, return the error
      console.error("Error adding enhanced transaction:", error);
      return { success: false, error: error.message };
    }
  }
};

// Get transactions for a specific symbol across all portfolios
export const getSymbolTransactions = async (userId, symbol) => {
  try {
    // First get all user portfolios
    const portfoliosResult = await getUserPortfolios(userId);
    if (!portfoliosResult.success) {
      return { success: false, error: "Failed to get portfolios" };
    }

    const allTransactions = [];

    // Get transactions for each portfolio
    for (const portfolio of portfoliosResult.data) {
      const q = query(
        collection(db, "transactions"),
        where("portfolioId", "==", portfolio.id),
        where("symbol", "==", symbol),
        orderBy("date", "desc")
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        allTransactions.push({
          id: doc.id,
          ...doc.data(),
          portfolioName: portfolio.name,
        });
      });
    }

    // Sort by date
    allTransactions.sort((a, b) => b.date.toDate() - a.date.toDate());

    return { success: true, data: allTransactions };
  } catch (error) {
    console.error("Error getting symbol transactions:", error);
    return { success: false, error: error.message };
  }
};

// ===== STOCK PERFORMANCE OPERATIONS =====

// Get stock performance across all user portfolios
export const getStockPerformance = async (userId, symbol) => {
  try {
    const symbolTransactions = await getSymbolTransactions(userId, symbol);
    if (!symbolTransactions.success) {
      return { success: false, error: "Failed to get transactions" };
    }

    const performance = calculateStockPerformance(symbolTransactions.data);
    return { success: true, data: performance };
  } catch (error) {
    console.error("Error getting stock performance:", error);
    return { success: false, error: error.message };
  }
};

// Get all holdings of a specific stock
export const getStockHoldings = async (userId = null, symbol = null) => {
  try {
    // If no userId provided, get all portfolios (for current system without user auth)
    let portfoliosResult;
    if (userId) {
      portfoliosResult = await getUserPortfolios(userId);
    } else {
      // Get all portfolios without user filter
      const q = query(
        collection(db, "portfolios"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const portfolios = [];
      querySnapshot.forEach((doc) => {
        portfolios.push({ id: doc.id, ...doc.data() });
      });
      portfoliosResult = { success: true, data: portfolios };
    }

    if (!portfoliosResult.success) {
      return { success: false, error: "Failed to get portfolios" };
    }

    const holdings = [];

    for (const portfolio of portfoliosResult.data) {
      // Get all investments for this portfolio
      let q;
      if (symbol) {
        q = query(
          collection(db, "investments"),
          where("portfolioId", "==", portfolio.id),
          where("symbol", "==", symbol)
        );
      } else {
        q = query(
          collection(db, "investments"),
          where("portfolioId", "==", portfolio.id)
        );
      }

      const querySnapshot = await getDocs(q);

      for (const doc of querySnapshot.docs) {
        const investment = { id: doc.id, ...doc.data() };

        // Get all transactions for this investment
        const transactionsQuery = query(
          collection(db, "transactions"),
          where("investmentId", "==", investment.id),
          where("portfolioId", "==", portfolio.id)
        );

        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactions = [];
        transactionsSnapshot.forEach((txnDoc) => {
          transactions.push({ id: txnDoc.id, ...txnDoc.data() });
        });

        // Calculate shares from transactions
        let totalShares = 0;
        let totalInvested = 0;
        let totalFees = 0;

        transactions.forEach((txn) => {
          if (txn.type === "buy") {
            totalShares += txn.shares || 0;
            totalInvested += txn.totalAmount || 0;
            totalFees += txn.fees || 0;
          } else if (txn.type === "sell") {
            totalShares -= txn.shares || 0;
            // For sells, reduce invested amount proportionally
            if (totalShares > 0) {
              const sellRatio =
                (txn.shares || 0) / (totalShares + (txn.shares || 0));
              totalInvested -= totalInvested * sellRatio;
            }
            totalFees += txn.fees || 0;
          }
        });

        // Only include holdings with shares > 0
        if (totalShares > 0) {
          const averageCost = totalShares > 0 ? totalInvested / totalShares : 0;
          const currentValue = totalShares * (investment.currentPrice || 0);
          const totalReturn = currentValue - totalInvested;
          const returnPercentage =
            totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0;

          holdings.push({
            ...investment,
            portfolioName: portfolio.name,
            portfolioId: portfolio.id,
            totalShares: totalShares,
            totalInvested: totalInvested,
            averageCost: averageCost,
            currentValue: currentValue,
            totalReturn: totalReturn,
            returnPercentage: returnPercentage,
            totalFees: totalFees,
            transactionCount: transactions.length,
          });
        }
      }
    }

    return { success: true, data: holdings };
  } catch (error) {
    console.error("Error getting stock holdings:", error);
    return { success: false, error: error.message };
  }
};

// Get all stock holdings across all portfolios (alias for getStockHoldings without symbol)
export const getAllStockHoldings = async (userId) => {
  return getStockHoldings(userId);
};

// ===== FILTERED LISTS OPERATIONS =====

// Get portfolio gainers (positive returns)
export const getPortfolioGainers = async (portfolioId) => {
  try {
    const q = query(
      collection(db, "investments"),
      where("portfolioId", "==", portfolioId),
      where("totalReturn", ">", 0),
      orderBy("totalReturn", "desc")
    );

    const querySnapshot = await getDocs(q);
    const gainers = [];
    querySnapshot.forEach((doc) => {
      gainers.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: gainers };
  } catch (error) {
    console.error("Error getting portfolio gainers:", error);
    return { success: false, error: error.message };
  }
};

// Get portfolio losers (negative returns)
export const getPortfolioLosers = async (portfolioId) => {
  try {
    const q = query(
      collection(db, "investments"),
      where("portfolioId", "==", portfolioId),
      where("totalReturn", "<", 0),
      orderBy("totalReturn", "asc")
    );

    const querySnapshot = await getDocs(q);
    const losers = [];
    querySnapshot.forEach((doc) => {
      losers.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: losers };
  } catch (error) {
    console.error("Error getting portfolio losers:", error);
    return { success: false, error: error.message };
  }
};

// Get investments by type
export const getInvestmentsByType = async (portfolioId, type) => {
  try {
    const q = query(
      collection(db, "investments"),
      where("portfolioId", "==", portfolioId),
      where("type", "==", type),
      orderBy("currentValue", "desc")
    );

    const querySnapshot = await getDocs(q);
    const investments = [];
    querySnapshot.forEach((doc) => {
      investments.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: investments };
  } catch (error) {
    console.error("Error getting investments by type:", error);
    return { success: false, error: error.message };
  }
};

// ===== UTILITY FUNCTIONS =====

// Calculate position from transactions
const calculatePositionFromTransactions = (transactions) => {
  let totalShares = 0;
  let totalCost = 0;
  let totalFees = 0;

  transactions.forEach((txn) => {
    if (txn.type === "buy") {
      totalShares += txn.shares;
      totalCost += txn.totalAmount;
      totalFees += txn.fees;
    } else if (txn.type === "sell") {
      totalShares -= txn.shares;
      totalCost -= (totalCost / (totalShares + txn.shares)) * txn.shares; // FIFO
      totalFees += txn.fees;
    }
  });

  const averageCost = totalShares > 0 ? totalCost / totalShares : 0;

  return {
    totalShares,
    totalCost,
    averageCost,
    totalFees,
  };
};

// Calculate updated position after new transaction
const calculateUpdatedPosition = (investment, transaction) => {
  const currentShares = investment.totalShares || 0;
  const currentCost = investment.totalCost || 0;

  let newShares = currentShares;
  let newCost = currentCost;

  if (transaction.type === "buy") {
    newShares = currentShares + transaction.shares;
    newCost = currentCost + transaction.totalAmount;
  } else if (transaction.type === "sell") {
    newShares = currentShares - transaction.shares;
    newCost = currentCost - (currentCost / currentShares) * transaction.shares;
  }

  const newAverageCost = newShares > 0 ? newCost / newShares : 0;
  const newValue = newShares * (investment.currentPrice || 0);
  const newReturn = newValue - newCost;
  const newReturnPercentage = newCost > 0 ? (newReturn / newCost) * 100 : 0;

  return {
    totalShares: newShares,
    totalCost: newCost,
    averageCost: newAverageCost,
    currentValue: newValue,
    totalReturn: newReturn,
    returnPercentage: newReturnPercentage,
  };
};

// Calculate updated portfolio after new transaction
const calculateUpdatedPortfolio = (portfolio, transaction) => {
  const currentCash = portfolio.cashAvailable || 0;
  let newCash = currentCash;

  if (transaction.type === "buy") {
    newCash = currentCash - transaction.totalAmount - transaction.fees;
  } else if (transaction.type === "sell") {
    newCash = currentCash + transaction.totalAmount - transaction.fees;
  } else if (transaction.type === "dividend") {
    newCash = currentCash + transaction.totalAmount;
  }

  return {
    cashAvailable: newCash,
  };
};

// Calculate stock performance from transactions
const calculateStockPerformance = (transactions) => {
  let totalShares = 0;
  let totalInvested = 0;
  let totalSold = 0;
  let totalDividends = 0;
  let totalFees = 0;

  transactions.forEach((txn) => {
    if (txn.type === "buy") {
      totalShares += txn.shares;
      totalInvested += txn.totalAmount;
      totalFees += txn.fees;
    } else if (txn.type === "sell") {
      totalShares -= txn.shares;
      totalSold += txn.totalAmount;
      totalFees += txn.fees;
    } else if (txn.type === "dividend") {
      totalDividends += txn.totalAmount;
    }
  });

  const netInvestment = totalInvested - totalSold;
  const totalReturn = totalDividends - totalFees;
  const returnPercentage =
    netInvestment > 0 ? (totalReturn / netInvestment) * 100 : 0;

  return {
    symbol: transactions[0]?.symbol,
    totalShares,
    totalInvested: netInvestment,
    totalDividends,
    totalFees,
    totalReturn,
    returnPercentage,
    transactions: transactions.length,
  };
};

// ===== USER PREFERENCES OPERATIONS =====

// Save user preferences (currency, calculation method, etc.)
export const saveUserPreferences = async (userId, preferences) => {
  try {
    const userPrefsRef = doc(db, "userPreferences", userId);
    await setDoc(userPrefsRef, {
      ...preferences,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    // If portfolio currency is being updated, also update all user's portfolios
    if (preferences.portfolioCurrency) {
      console.log("=== UPDATING PORTFOLIO CURRENCY ===");
      console.log("New portfolio currency:", preferences.portfolioCurrency);
      try {
        // Get all portfolios for this user
        const portfoliosQuery = query(
          collection(db, "portfolios"),
          where("userId", "==", userId)
        );
        const portfoliosSnapshot = await getDocs(portfoliosQuery);
        
        console.log(`Found ${portfoliosSnapshot.size} portfolios for user ${userId}`);
        
        // Update each portfolio's currency
        const updatePromises = [];
        portfoliosSnapshot.forEach((portfolioDoc) => {
          console.log(`Updating portfolio ${portfolioDoc.id} currency to ${preferences.portfolioCurrency}`);
          updatePromises.push(
            updateDoc(doc(db, "portfolios", portfolioDoc.id), {
              currency: preferences.portfolioCurrency,
              updatedAt: serverTimestamp(),
            })
          );
        });
        
        await Promise.all(updatePromises);
        console.log(`Successfully updated currency to ${preferences.portfolioCurrency} for ${updatePromises.length} portfolios`);
      } catch (error) {
        console.error("Error updating portfolio currencies:", error);
        // Don't fail the whole operation if portfolio update fails
      }
    }
    
    console.log("User preferences saved for user:", userId);
    return { success: true };
  } catch (error) {
    console.error("Error saving user preferences:", error);
    return { success: false, error: error.message };
  }
};

// Get user preferences
export const getUserPreferences = async (userId) => {
  try {
    const userPrefsRef = doc(db, "userPreferences", userId);
    const userPrefsSnap = await getDoc(userPrefsRef);
    
    if (userPrefsSnap.exists()) {
      const data = userPrefsSnap.data();
      return { 
        success: true, 
        data: {
          marketCurrency: data.marketCurrency || "USD",
          portfolioCurrency: data.portfolioCurrency || "USD",
          isTtwror: data.isTtwror || false,
          ...data
        }
      };
    } else {
      // Return default preferences if none exist
      return { 
        success: true, 
        data: {
          marketCurrency: "USD",
          portfolioCurrency: "USD",
          isTtwror: false
        }
      };
    }
  } catch (error) {
    console.error("Error getting user preferences:", error);
    return { success: false, error: error.message };
  }
};


// Export enhanced functions as primary
export const createPortfolio = createPortfolioEnhanced;
export const addInvestment = addInvestmentEnhanced;
export const addTransaction = addTransactionEnhanced;

// Re-export legacy functions for compatibility
export {
  getUserPortfolios,
  getPortfolioInvestments,
  getInvestmentTransactions,
  getPortfolioTransactions,
  updateInvestment,
  getPortfolioCashAvailable,
};

// Set portfolio cash available
export const setPortfolioCashAvailable = async (portfolioId, cash) => {
  try {
    await updateDoc(doc(db, "portfolios", portfolioId), {
      cashAvailable: cash,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error setting portfolio cash:", error);
    return { success: false, error: error.message };
  }
};

// Add cash transaction (deposit or withdrawal)
export const addCashTransaction = async (portfolioId, transactionData) => {
  try {
    const { type, amount, description, date } = transactionData;

    // Validate transaction data
    if (!type || !amount || amount <= 0) {
      return { success: false, error: "Invalid transaction data" };
    }

    // Handle date conversion
    let transactionDate;
    if (date) {
      try {
        if (typeof date === "string") {
          const parsedDate = new Date(date);
          if (!isNaN(parsedDate.getTime())) {
            transactionDate = parsedDate;
          } else {
            transactionDate = serverTimestamp();
          }
        } else if (date instanceof Date) {
          if (!isNaN(date.getTime())) {
            transactionDate = date;
          } else {
            transactionDate = serverTimestamp();
          }
        } else {
          transactionDate = serverTimestamp();
        }
      } catch (error) {
        console.warn("Invalid date provided, using serverTimestamp:", error);
        transactionDate = serverTimestamp();
      }
    } else {
      transactionDate = serverTimestamp();
    }

    // Get current portfolio
    const portfolioDoc = await getDoc(doc(db, "portfolios", portfolioId));
    if (!portfolioDoc.exists()) {
      return { success: false, error: "Portfolio not found" };
    }

    const currentCash = portfolioDoc.data().cashAvailable || 0;
    let newCash = currentCash;

    // Calculate new cash balance
    if (type === "deposit") {
      newCash = currentCash + amount;
    } else if (type === "withdrawal") {
      if (currentCash < amount) {
        return { success: false, error: "Insufficient funds" };
      }
      newCash = currentCash - amount;
    } else {
      return { success: false, error: "Invalid transaction type" };
    }

    // Add cash transaction record
    const cashTxnRef = await addDoc(collection(db, "cashTransactions"), {
      portfolioId,
      type,
      amount,
      description:
        description ||
        `${type === "deposit" ? "Deposit" : "Withdrawal"} of $${amount}`,
      previousBalance: currentCash,
      newBalance: newCash,
      date: transactionDate,
      createdAt: serverTimestamp(),
    });

    // Update portfolio cash balance
    await updateDoc(doc(db, "portfolios", portfolioId), {
      cashAvailable: newCash,
      updatedAt: serverTimestamp(),
    });

    console.log("Cash transaction added with ID:", cashTxnRef.id);
    return {
      success: true,
      transactionId: cashTxnRef.id,
      newBalance: newCash,
      previousBalance: currentCash,
    };
  } catch (error) {
    console.error("Error adding cash transaction:", error);
    return { success: false, error: error.message };
  }
};

// Get cash transaction history for a portfolio
export const getCashTransactionHistory = async (
  portfolioId,
  limitCount = 50
) => {
  try {
    const q = query(
      collection(db, "cashTransactions"),
      where("portfolioId", "==", portfolioId),
      orderBy("date", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const transactions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate?.() || data.date || new Date(),
      });
    });

    return { success: true, data: transactions };
  } catch (error) {
    console.error("Error getting cash transaction history:", error);
    return { success: false, error: error.message };
  }
};

// Get cash balance history for graphing
export const getCashBalanceHistory = async (portfolioId, days = 30) => {
  try {
    // Validate portfolioId
    if (!portfolioId) {
      console.warn("getCashBalanceHistory called with undefined portfolioId");
      return { success: true, data: [] };
    }

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const q = query(
      collection(db, "cashTransactions"),
      where("portfolioId", "==", portfolioId),
      where("date", ">=", startDate),
      orderBy("date", "asc")
    );

    const querySnapshot = await getDocs(q);
    const transactions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate?.() || data.date || new Date(),
      });
    });

    // Sort by date and calculate running balance
    transactions.sort((a, b) => a.date - b.date);

    const balanceHistory = [];
    let runningBalance = 0;

    // Get current portfolio balance first
    const portfolioDoc = await getDoc(doc(db, "portfolios", portfolioId));
    let currentBalance = 0;
    if (portfolioDoc.exists()) {
      currentBalance = portfolioDoc.data().cashAvailable || 0;
    }

    // If no transactions, create a simple history with current balance
    if (transactions.length === 0) {
      balanceHistory.push({
        date: startDate,
        balance: currentBalance,
        type: "initial",
      });
      balanceHistory.push({
        date: new Date(),
        balance: currentBalance,
        type: "current",
      });
    } else {
      // Add initial balance point
      runningBalance = transactions[0].previousBalance;
      balanceHistory.push({
        date: startDate,
        balance: runningBalance,
        type: "initial",
      });

      // Add balance points for each transaction
      transactions.forEach((txn) => {
        balanceHistory.push({
          date: txn.date,
          balance: txn.newBalance,
          type: txn.type,
          amount: txn.amount,
          description: txn.description,
        });
        runningBalance = txn.newBalance;
      });

      // Add current balance point if no recent transactions
      if (
        balanceHistory.length === 0 ||
        balanceHistory[balanceHistory.length - 1].date < new Date()
      ) {
        balanceHistory.push({
          date: new Date(),
          balance: currentBalance,
          type: "current",
        });
      }
    }

    // Ensure we have at least one data point
    if (balanceHistory.length === 0) {
      balanceHistory.push({
        date: new Date(),
        balance: 0,
        type: "empty",
      });
    }

    return { success: true, data: balanceHistory };
  } catch (error) {
    console.error("Error getting cash balance history:", error);
    // Return a safe default instead of failing
    return {
      success: true,
      data: [
        {
          date: new Date(),
          balance: 0,
          type: "error",
        },
      ],
    };
  }
};
